import _ from "lodash";
import React from "react";

// components
import UnitSelectionDropdown from "./components/UnitSelectionDropdown";
import UnitSelectionList from "./components/UnitSelectionList";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import RemoveSubjectModal from "AppComponents/RemoveSubjectModal";

// third party
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Trans } from "react-i18next";

// design system
import { Button, Modal, RadioButton } from "@toddle-edu/ds-web";

// modules
import { getFlatUnitPlanFeedForAssessmentEnhancer } from "Assessments/modules/AssessmentQueryEnhancers";
import {
  updateAssessmentInRedux,
  updateAssessment,
  updateAssessmentInCache,
  getRemovedSubjectDependencies,
} from "Assessments/modules/AssessmentModule";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";

// utils
import {
  getRethumb,
  getIsUnitPlanUpdateModalEnabled,
} from "ClassRoom/modules/utils.js";
import { getDefaultUnitPlanImage } from "UnitPlans/modules/Utils";
import { getAttachmentUrlFromFieldValue } from "Utils";
import {
  getFieldValue,
  getIsUnitPlanFieldDisabled,
} from "Assessments/modules/Utils";
import {
  getIsCourseBasedGradingEnabled,
  getIsLearningCoursesEnabled,
} from "LearningCourses/modules/Utils";

// constants
import {
  ASC,
  UNIT_TYPE,
  CURRICULUM_TYPE_PYP,
  MYP_SUBJECT_SPECIFIC,
} from "Constants/stringConstants";

// scss
import classes from "./UnitSelection.scss";

const MAX_UNIT_PLAN_FEED_LIMIT = 10;
const unitImageDimensions = { height: 32, width: 32 };
const selectedUnitImageDimensions = { height: 24, width: 24 };

const UI_TYPES = {
  DROPDOWN: "DROPDOWN",
  LIST: "LIST",
};

const styles = {
  containerStyle: {
    flexDirection: "row",
    alignItems: "center",
  },
};

const initialUnitPlanModalstate = {
  isVisible: false,
  action: null,
  selectedOption: null,
  updatedUnitPlan: {},
};

const UNIT_DEPENDENT_FIELDS = [
  "inquiryQuestions",
  "inquiryQuestionsV2",
  "lois",
];

const components = { b: <b /> };

const getUnitPlanUpdateModalData = ({ t, newUnit, previousUnit }) => {
  const changeUnitMessage = (
    <Trans
      i18nKey={"classRoom:change_associated_unit_message"}
      components={components}
      values={{ newUnit, previousUnit }}
    >
      You are changing the associated unit of this task to
      <b>&apos;{{ newUnit }}&apos;</b>. A copy of this task already exists in
      the previous unit <b>&apos;{{ previousUnit }}&apos;</b>.
    </Trans>
  );

  const removeUnitMessage = (
    <Trans
      i18nKey={"classRoom:remove_associated_unit_message"}
      components={components}
      values={{ previousUnit }}
    >
      You are removing the unit associated with this task. A copy of this task
      already exists in the previous unit <b>&apos;{{ previousUnit }}&apos;</b>.
    </Trans>
  );

  return {
    CHANGE_UNIT: {
      title: t("classRoom:change_associated_unit"),
      message: changeUnitMessage,
      option1: t("classRoom:move_copy_to_new_unit"),
      option2: t("classRoom:keep_copy_in_previous_unit_and_create_new_copy"),
      buttonText: `${t("common:change")} ${t("common:lowercase", {
        text: t("common:unit"),
      })}`,
    },
    REMOVE_UNIT: {
      title: t("classRoom:remove_associated_unit"),
      message: removeUnitMessage,
      option1: t("classRoom:remove_copy_from_previous_unit"),
      option2: t("classRoom:keep_copy_in_previous_unit"),
      buttonText: `${t("common:remove")} ${t("common:lowercase", {
        text: t("common:unit"),
      })}`,
    },
  };
};

const getFormattedUnitPlan = ({
  t,
  unitPlan,
  cardDimensions = selectedUnitImageDimensions,
}) => {
  const { id, title, image, resolvedGrades } = unitPlan || {};
  const gradesTitleArr = _.map(resolvedGrades, ({ name }) => name);
  const subLabel = _.join(gradesTitleArr, ", ");
  const imageValue = getAttachmentUrlFromFieldValue({ fieldValue: image });

  return {
    value: id,
    label: title?.value || t("common:untitled"),
    subLabel,
    backgroundImage: getDefaultUnitPlanImage({
      id,
      imageUrl: imageValue,
      getRethumb: getRethumb({ cardDimensions }),
    }),
  };
};

const getUnitSelectionOptions = ({ t, unitPlans }) => {
  return _.map(unitPlans, unitPlan =>
    getFormattedUnitPlan({ t, unitPlan, cardDimensions: unitImageDimensions })
  );
};

const getUnitSelectionOptionsMemoized = _.memoize(
  getUnitSelectionOptions,
  ({ unitPlans }) => {
    const unitPlansData = _.map(unitPlans, unitPlan => {
      const { id, title, image, resolvedGrades } = unitPlan || {};
      const gradeNames = _.map(resolvedGrades, "name");

      return {
        id,
        title: title?.value,
        imageId: image?.id,
        gradeNames,
      };
    });

    return JSON.stringify({
      unitPlans: unitPlansData,
    });
  }
);

const isLeFromLeLibrary = ({ creationMode, sourceFrom }) => {
  return (
    (creationMode === "ASSIGN_TO_CLASS" && sourceFrom === "CLASSROOM") ||
    creationMode === "IMPORT_LE_FROM_LE_LIBRARY"
  );
};

const INITIAL_DIALOG_INFO_STATE = {
  action: "",
  params: {},
};

class UnitSelection extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectedUnitPlan: {},
      isVisible: false,
      unitPlanUpdateModal: initialUnitPlanModalstate,
      shouldUpdateValue: false,
      currentDialogInfo: INITIAL_DIALOG_INFO_STATE,
    };
  }

  componentDidMount = () => {
    const {
      preSelectedUnitPlan,
      value,
      creationMode,
      sourceFrom,
      courseId,
      assessmentMode,
      assessmentFields,
    } = this.props;
    if (!_.isEmpty(preSelectedUnitPlan)) {
      /** 
       * cases:
        - Create/Edit assessment in unit plan

       * behaviour:
        - we will get preSelectedUnitPlan under assessmentDetails -> unitPlan
        - update that unitPlan in cache
       */
      if (value) {
        const subjectValue = getFieldValue({
          fields: assessmentFields,
          uid: "subjects",
        });
        this.setSelectedUnit({
          callUpdateAssessmentMutation: false,
          updateSubjectsValue: _.isEmpty(subjectValue),
        });
      } else {
        this.updateValue(preSelectedUnitPlan?.id, preSelectedUnitPlan);
        this.setState({ shouldUpdateValue: true });
      }
    } else if (value) {
      if (isLeFromLeLibrary({ creationMode, sourceFrom })) {
        /** 
          * cases:
            - Import from LE library
            - Assign to students from LE library

          * behaviour:
            - There will be unitPlan field value in allFields initially as we are creating a copy from LE
            - If current selected course exists in the current unitPlan courses
                then update unitPlan in local state and call updateAssessment mutation
                set first subject of unitPlan as assessment subject
            - else remove unit plan
        */
        const currentUnitPlan = this.getResolvedUnitPlan();
        const unitPlanCourseIds = _.map(
          _.get(currentUnitPlan, "courses", []),
          ({ id }) => id
        );

        if (
          !_.isEmpty(unitPlanCourseIds) &&
          _.includes(unitPlanCourseIds, courseId)
        ) {
          this.setSelectedUnit({
            callUpdateAssessmentMutation: false,
            updateSubjectsValue: false,
          });
        } else {
          this.updateValue("", null);
          this.setState({ shouldUpdateValue: true });
        }
      } else {
        /** 
           * cases:
            - Edit assessment in class stream
            - Import from Unit
            - Assign to students from unit plan

          * behaviour:
            - There will be unitPlan field value in allFields initially as we are updating assessment or creating a copy from an assessment
            - update that unitPlan in local state
        */
        this.setSelectedUnit({
          callUpdateAssessmentMutation: false,
          updateSubjectsValue: false,
        });
      }
    } else {
      /** 
       * cases:
        - CREATE LE, CREATE FA, CREATE SA, CREATE Assessment in class stream
        - Edit LE / Assessment when no unit plan is selected on creation

       * behaviour:
        - unitPlan field value will be null initially
        - set first subject of course as assessment subject
       */
      if (assessmentMode === "create") {
        setTimeout(() => {
          this.setCourseSubject();
        });
      }
    }
  };

  componentDidUpdate = prevProps => {
    const { value, isLocalUpdate } = this.props;
    const { shouldUpdateValue } = this.state;

    if (prevProps.value != value) {
      /**
       * update selectedUnitPlan in local state and call updateAssessment mutation
       * If unitPlan is updated then set first subject of new selected unitPlan as assessment subject
       */
      this.setSelectedUnit({
        callUpdateAssessmentMutation: !isLocalUpdate && shouldUpdateValue,
        updateSubjectsValue: !isLocalUpdate && shouldUpdateValue,
      });
      // If unitPlan is removed then set first subject of course as assessment subject
      if (!value) {
        this.setCourseSubject(true);
      }
    }

    // when more than one classes selected in calendar options will be empty on mount
    if (
      prevProps.options != this.props.options &&
      _.isEmpty(prevProps.options) &&
      !_.isEmpty(this.props.options)
    ) {
      this.setCourseSubject();
    }
  };

  getResolvedUnitPlan = () => {
    const { assessmentFields, value, isLocalUpdate, unitPlans } = this.props;
    const unitPlanFieldObj =
      _.find(assessmentFields, { uid: "unitPlan" }) || {};
    const resolvedMinimalTree = _.get(
      unitPlanFieldObj,
      "resolvedMinimalTree",
      {}
    );

    if (isLocalUpdate) {
      return _.find(unitPlans, { id: value }) || {};
    }

    return _.get(resolvedMinimalTree, "unitPlan", {}) || {};
  };

  getUnitPlanUpdateSelectedOption = () => {
    const {
      unitPlanUpdateModal: { selectedOption },
    } = this.state;

    switch (selectedOption) {
      case "COPY":
        return { isCopyIntoUnitPlan: true };
      case "MOVE":
        return { isMoveIntoUnitPlan: true };
      case "REMOVE":
        return { isDeleteFromUnitPlan: true };
      default:
        return {};
    }
  };

  shouldUpdateSubject = unitPlanSubjectIds => {
    return !_.isEmpty(unitPlanSubjectIds) && _.size(unitPlanSubjectIds) === 1;
  };

  // Called when unit plan field is updated
  setSelectedUnit = async ({
    callUpdateAssessmentMutation,
    updateSubjectsValue,
    writeToCache = false,
  }) => {
    const {
      t,
      updateAssessment,
      value,
      fieldId,
      updateInputField,
      unitPlanFields,
      updateAssessmentInCache,
      fieldKey: key,
      field_list,
      resourceFields,
      unitPlanTemplateId,
      updateAssessmentInRedux,
      assessmentFields,
      subjectsField,
      isCourseBasedGrading,
    } = this.props;
    const selectedUnitPlan = this.getResolvedUnitPlan();

    // local state update
    this.setState({
      selectedUnitPlan: !_.isEmpty(selectedUnitPlan)
        ? getFormattedUnitPlan({
            t,
            unitPlan: selectedUnitPlan,
          })
        : {},
      unitPlanUpdateModal: initialUnitPlanModalstate,
    });

    // updateAssessment mutation call
    if (callUpdateAssessmentMutation) {
      await updateAssessment({
        id: fieldId,
        value: value || "",
        hasAddRemoveValues: false,
        unitPlanId: value || -1,
        ...this.getUnitPlanUpdateSelectedOption(),
      });

      if (writeToCache) {
        const fieldObj = _.find(resourceFields, { uid: key }) || {};
        const fieldTemplateObj = field_list?.[key];

        updateAssessmentInCache({
          fieldUID: key,
          params: { id: fieldObj?.id, value, uid: key },
          fieldObj,
          fieldTemplateObj,
          extraParams: selectedUnitPlan,
        });
      }
    }

    if (updateSubjectsValue && !isCourseBasedGrading) {
      // If unit plan is selected and if unit is tagged with single subject then select that subject
      const subjectValue = getFieldValue({
        fields: assessmentFields,
        uid: "subjects",
      });

      if (value) {
        const unitPlanSubjectIds = _.get(
          selectedUnitPlan,
          "subjects.value",
          []
        );
        const subjectFieldData =
          _.find(unitPlanFields, { uid: "subjects" }) || {};
        const unitPlanSubjects = _.get(
          subjectFieldData,
          `resolvedMinimalTree.subjects`,
          []
        );

        if (this.shouldUpdateSubject(unitPlanSubjectIds)) {
          if (
            !_.isEmpty(subjectsField) && // only update if subject field is present
            !_.isEmpty(unitPlanSubjectIds) &&
            _.size(unitPlanSubjects) === 1
          ) {
            updateInputField(
              { subjects: unitPlanSubjectIds },
              { nodes: _.map(unitPlanSubjects, node => ({ node })) },
              false
            );
          } else if (!_.isEmpty(subjectValue)) {
            this.clearSubjectSelection();
          }
        }
      }
    }

    if (
      _.isEmpty(unitPlanTemplateId) ||
      unitPlanTemplateId !== selectedUnitPlan?.templateId
    ) {
      updateAssessmentInRedux({
        unitPlanTemplateId: _.get(selectedUnitPlan, "templateId"),
      });
    }
  };

  clearSubjectSelection = () => {
    const { updateInputField, subjectsField } = this.props;

    if (!_.isEmpty(subjectsField)) {
      updateInputField({ subjects: [] }, { nodes: [] });
    }
  };

  setCourseSubject = (shouldClearSelection = false) => {
    const {
      options: subjects,
      updateInputField,
      currentCurriculumProgramType,
      subjectsField,
      isCourseBasedGrading,
    } = this.props;
    if (isCourseBasedGrading) return;
    const subject = _.first(subjects);
    const subjectId = _.get(subject, "value", null);

    if (
      !_.isEmpty(subjectsField) && // only update if subject field is present
      subjectId &&
      _.size(subjects) === 1 &&
      currentCurriculumProgramType !== CURRICULUM_TYPE_PYP
    ) {
      updateInputField({ subjects: [subjectId] }, { nodes: [subject] }, false);
    } else if (shouldClearSelection) {
      this.clearSubjectSelection();
    }
  };

  updateUnitDependentFields = () => {
    const { updateInputField, assessmentFields } = this.props;

    _.forEach(UNIT_DEPENDENT_FIELDS, field => {
      const fieldObj = _.find(assessmentFields, { uid: field }) || {};
      if (!_.isEmpty(fieldObj)) {
        const fieldValue = getFieldValue({
          fields: assessmentFields,
          uid: field,
        });
        switch (field) {
          case "lois":
          case "inquiryQuestionsV2": {
            if (!_.isEmpty(fieldValue)) {
              updateInputField({ [field]: [] }, { nodes: [] });
            }
            break;
          }
          case "inquiryQuestions": {
            let isValueEmpty = true;
            _.forEach(fieldValue, questions => {
              if (!_.isEmpty(questions)) {
                isValueEmpty = false;
              }
            });
            if (!isValueEmpty) {
              updateInputField({ [field]: [] }, { nodes: [] });
            }
            break;
          }
        }
      }
    });
  };

  handleCloseUnitPlanUpdateModal = () => {
    this.setState({
      unitPlanUpdateModal: initialUnitPlanModalstate,
    });
  };

  handleUpdateUnitModalSelection = () => {
    const { unitPlanUpdateModal } = this.state;
    const { updatedUnitPlan } = unitPlanUpdateModal;

    if (_.isEmpty(updatedUnitPlan)) {
      this.updateValue("", null);
      this.setState({ shouldUpdateValue: true });
      return;
    }

    this.updateValue(updatedUnitPlan.id, updatedUnitPlan);

    this.setState({
      shouldUpdateValue: true,
      unitPlanUpdateModal: {
        ...unitPlanUpdateModal,
        isVisible: false,
        action: null,
        updatedUnitPlan: {},
      },
    });
  };

  onChangeOrClearUnit = () => {
    const { currentDialogInfo } = this.state;
    const { action, params } = currentDialogInfo || {};
    const { unitPlanId } = params || {};

    switch (action) {
      case "CHANGE_UNIT":
        this.handleUnitSelection(unitPlanId);
        break;

      case "CLEAR_UNIT":
        this.handleClearSelection();
        break;
    }
    this.onClose();
  };

  onClose = () => {
    this.setState({ currentDialogInfo: INITIAL_DIALOG_INFO_STATE });
  };

  updateCurrentDialogInfo = params => {
    const { action, unitPlanSubjectIds, unitPlanId } = params;

    const {
      curriculumType,
      assessmentTools,
      assessmentFields,
      t,
      field_list,
      templateId,
      isLearningGoalsEvaluationEnabled,
      assessmentMode,
      step_list,
      isLocalUpdate,
      unitPlans,
      getRemovedSubjectDependencies,
      assessmentType,
    } = this.props;

    const { currentDialogInfo, selectedUnitPlan } = this.state;

    const assessmentSubjectIds = getFieldValue({
      fields: assessmentFields,
      uid: "subjects",
    });

    const removedSubjectIds = _.difference(
      assessmentSubjectIds,
      unitPlanSubjectIds
    );

    const updatedUnitPlan = _.find(unitPlans, { id: unitPlanId }) || {};
    const updatedUnitType = getFieldValue({
      fields: updatedUnitPlan?.allFields,
      uid: "unitType",
    });

    if (
      _.isEmpty(removedSubjectIds) &&
      updatedUnitType != MYP_SUBJECT_SPECIFIC
    ) {
      return false;
    }

    const isUnitChangeFromEmptyToSubjectSpecific =
      _.isEmpty(selectedUnitPlan) && updatedUnitType === MYP_SUBJECT_SPECIFIC;

    const { headerTitle, subTitle: subtitle, subjectDependencies = [] } =
      getRemovedSubjectDependencies({
        t,
        curriculumType,
        assessmentTools,
        assessmentFields,
        assessmentType,
        field_list,
        templateId,
        currentSubjectValue: assessmentSubjectIds,
        updatedSubjectValue: this.shouldUpdateSubject(unitPlanSubjectIds)
          ? unitPlanSubjectIds
          : [],
        sourceFrom: "UNIT_SELECTION_DROPDOWN",
        isLearningGoalsEvaluationEnabled,
        assessmentMode,
        step_list,
        updatedUnitType,
        isUnitChangeFromEmptyToSubjectSpecific,
      }) || {};

    if (
      !_.isEmpty(subjectDependencies) &&
      _.isEmpty(currentDialogInfo?.action) &&
      !isLocalUpdate
    ) {
      this.setState({
        currentDialogInfo: {
          action,
          params: {
            subjectDependencies,
            headerTitle,
            subtitle,
            unitPlanId,
          },
        },
      });

      return true;
    }

    return false;
  };

  handleUnitSelection = unitPlanId => {
    const { selectedUnitPlan } = this.state;
    const { unitPlans, assessmentMode, updateAssessmentInRedux } = this.props;
    const updatedUnitPlan = _.find(unitPlans, { id: unitPlanId }) || {};
    const unitPlanSubjectIds = _.get(updatedUnitPlan, "subjects.value", []);

    const isCurrentDialogInfoUpdated = this.updateCurrentDialogInfo({
      action: "CHANGE_UNIT",
      unitPlanSubjectIds: unitPlanSubjectIds
        ? _.castArray(unitPlanSubjectIds)
        : [],
      unitPlanId,
    });

    if (isCurrentDialogInfoUpdated) return;

    if (
      getIsUnitPlanUpdateModalEnabled() &&
      assessmentMode === "edit" &&
      !_.isEmpty(selectedUnitPlan) &&
      !_.isEmpty(updatedUnitPlan) &&
      selectedUnitPlan.id != updatedUnitPlan.id
    ) {
      this.setState({
        isVisible: false,
        unitPlanUpdateModal: {
          isVisible: true,
          action: "CHANGE_UNIT",
          updatedUnitPlan,
        },
      });
      return;
    }

    this.updateUnitDependentFields();
    this.updateValue(unitPlanId, updatedUnitPlan);
    updateAssessmentInRedux({
      unitPlanTemplateId: _.get(updatedUnitPlan, "templateId"),
    });
    this.setState({
      isVisible: false,
      shouldUpdateValue: true,
    });
  };

  handleClearSelection = e => {
    e?.stopPropagation();

    const { selectedUnitPlan } = this.state;
    const { assessmentMode, options: subjects } = this.props;

    const subject = _.first(subjects);
    const unitPlanSubjectIds = _.get(subject, "value", null);

    const isCurrentDialogInfoUpdated = this.updateCurrentDialogInfo({
      action: "CLEAR_UNIT",
      unitPlanSubjectIds: unitPlanSubjectIds
        ? _.castArray(unitPlanSubjectIds)
        : [],
    });

    if (isCurrentDialogInfoUpdated) return;

    if (
      getIsUnitPlanUpdateModalEnabled() &&
      assessmentMode === "edit" &&
      !_.isEmpty(selectedUnitPlan)
    ) {
      this.setState({
        isVisible: false,
        unitPlanUpdateModal: {
          isVisible: true,
          action: "REMOVE_UNIT",
          updatedUnitPlan: {},
        },
      });
      return;
    }

    this.updateUnitDependentFields();
    this.updateValue("", null);
    this.setState({ shouldUpdateValue: true });
  };

  handleSearchTextChange = unitSearchText => {
    const { updateAssessmentInRedux } = this.props;
    updateAssessmentInRedux({ unitSearchText });
  };

  handleVisibleChange = isVisible => {
    this.setState({ isVisible });
    this.handleSearchTextChange("");
  };

  handleCopyUnitPlan = () => {
    const { unitPlanUpdateModal } = this.state;
    this.setState({
      unitPlanUpdateModal: {
        ...unitPlanUpdateModal,
        selectedOption: "COPY",
      },
    });
  };

  handleMoveOrRemoveUnitPlan = () => {
    const { unitPlanUpdateModal } = this.state;
    const { action } = unitPlanUpdateModal;

    this.setState({
      unitPlanUpdateModal: {
        ...unitPlanUpdateModal,
        selectedOption: action === "CHANGE_UNIT" ? "MOVE" : "REMOVE",
      },
    });
  };

  renderUnitPlanUpdateModal = () => {
    const {
      selectedUnitPlan,
      unitPlanUpdateModal: { action, updatedUnitPlan, selectedOption },
    } = this.state;
    const { t } = this.props;

    const unitPlanUpdateModalData = getUnitPlanUpdateModalData({
      t,
      newUnit: updatedUnitPlan?.title?.value,
      previousUnit: selectedUnitPlan?.label,
    });

    const messageClasses = classNames("text-body-1", classes.message);

    return (
      <Modal
        isOpen={true}
        width="530px"
        height="max-content"
        onClose={this.handleCloseUnitPlanUpdateModal}
      >
        <Modal.Header
          title={unitPlanUpdateModalData[action].title}
          isClosable={true}
        />
        <Modal.Body>
          <div className={classes.modalBody}>
            <div className={messageClasses}>
              {unitPlanUpdateModalData?.[action]?.message || ""}
            </div>
            <div className={classes.selectionsContainer}>
              <div className="text-label-2">
                {t("classRoom:what_to_do_with_existing_copy")}
              </div>
              <div className={classes.option}>
                <RadioButton
                  checked={_.includes(["MOVE", "REMOVE"], selectedOption)}
                  onChange={this.handleMoveOrRemoveUnitPlan}
                />
                <div className="text-body-1">
                  {unitPlanUpdateModalData[action].option1}
                </div>
              </div>
              <div className={classes.option}>
                <RadioButton
                  checked={selectedOption === "COPY"}
                  onChange={this.handleCopyUnitPlan}
                />
                <div className="text-body-1">
                  {unitPlanUpdateModalData[action].option2}
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.modalFooter}>
            <Button
              _legacy_variant="outlined-subtle"
              variant="neutral"
              type="outlined"
              onClick={this.handleCloseUnitPlanUpdateModal}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={this.handleUpdateUnitModalSelection}
              disabled={_.isEmpty(selectedOption)}
            >
              {unitPlanUpdateModalData[action].buttonText}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  renderUnitSelectionUI = () => {
    const {
      t,
      uiType = UI_TYPES.DROPDOWN,
      unitPlans,
      isLoading,
      isLoadingMoreUnitPlans,
      hasNextPage,
      loadMoreUnitPlanFeed,
      networkStatus,
    } = this.props;

    const { isVisible, selectedUnitPlan, error } = this.state;

    const unitPlanOptions = getUnitSelectionOptionsMemoized({ t, unitPlans });

    switch (uiType) {
      case UI_TYPES.DROPDOWN: {
        return (
          <UnitSelectionDropdown
            {...this.props}
            handleVisibleChange={this.handleVisibleChange}
            handleClearSelection={this.handleClearSelection}
            handleSearchTextChange={this.handleSearchTextChange}
            handleUnitSelection={this.handleUnitSelection}
            isVisible={isVisible}
            selectedUnitPlan={selectedUnitPlan}
            unitPlanOptions={unitPlanOptions}
            error={error}
          />
        );
      }

      case UI_TYPES.LIST:
        return (
          <UnitSelectionList
            {...this.props}
            unitPlanOptions={unitPlanOptions}
            handleSearchTextChange={this.handleSearchTextChange}
            handleUnitSelection={this.handleUnitSelection}
            isLoading={isLoading}
            isLoadingMoreUnitPlans={isLoadingMoreUnitPlans}
            hasNextPage={hasNextPage}
            loadMoreFeed={loadMoreUnitPlanFeed}
            networkStatus={networkStatus}
            selectedUnitPlan={selectedUnitPlan}
          />
        );
      default:
        return null;
    }
  };

  renderEdit = () => {
    const {
      t,
      parentId,
      parentType,
      portalType,
      updateInputField,
    } = this.props;

    const {
      currentDialogInfo,
      unitPlanUpdateModal: { isVisible: isUnitPlanUpdateModalVisible },
    } = this.state;

    const { headerTitle, subtitle, subjectDependencies } =
      currentDialogInfo?.params || {};

    return (
      <>
        {this.renderUnitSelectionUI()}

        {currentDialogInfo?.action ? (
          <RemoveSubjectModal
            subjectDependencies={subjectDependencies}
            headerTitle={headerTitle}
            subtitle={subtitle}
            parentId={parentId}
            parentType={parentType}
            portalType={portalType}
            updateInputField={updateInputField}
            removeButtonLabel={t("common:change")}
            onRemove={this.onChangeOrClearUnit}
            onClose={this.onClose}
          />
        ) : null}

        {isUnitPlanUpdateModalVisible ? this.renderUnitPlanUpdateModal() : null}
      </>
    );
  };

  renderView = () => this.renderUnitSelectionUI();
}

UnitSelection.propTypes = {
  ...UIBaseComponent.propTypes,
  isLocalUpdate: PropTypes.bool,
  testId: PropTypes.string,
};

UnitSelection.defaultProps = {
  ...UIBaseComponent.defaultProps,
  isLocalUpdate: false,
  testId: "IBComponents-unitSelectionDropdown",
};

const mapActionCreators = {
  updateAssessmentInRedux,
  updateAssessment,
  updateAssessmentInCache,
  getRemovedSubjectDependencies,
};

const mapStateToProps = (state, ownProps) => {
  const { mode, portalType, assessmentFields, learningCourseId } = ownProps;

  const unitSearchText = _.get(
    state,
    "assessment.assessmentData.unitSearchText",
    ""
  );

  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course"
  );

  const courseId = ownProps?.selectedCourseId || selectedCourseId;

  const assessmentId =
    ownProps?.parentId || _.get(ownProps, "params.assessmentId");

  const subjectsField = _.find(assessmentFields, { uid: "subjects" });

  const isUnitPlanFieldDisabled = getIsUnitPlanFieldDisabled(ownProps);

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  const filterKey = isLearningCoursesEnabled
    ? "learningCourseUnitPlanFilters"
    : "filters";

  const unitPlanFeedVariables = {
    id: isLearningCoursesEnabled ? learningCourseId : courseId,
    type: isLearningCoursesEnabled ? "LEARNING_COURSE" : "COURSE",
    first: MAX_UNIT_PLAN_FEED_LIMIT,
    orderBy: UNIT_TYPE,
    orderByDirection: ASC,
    showCollaborators: false,
    [filterKey]: {
      searchText: unitSearchText,
    },
    portalType,
  };

  return {
    courseId,
    assessmentId,
    unitSearchText,
    unitPlanFeedVariables,
    containerStyle: styles.containerStyle,
    skipUnitPlanFeedQuery:
      mode === "view" ||
      (isLearningCoursesEnabled
        ? _.isEmpty(learningCourseId)
        : _.isEmpty(courseId)),
    unitPlanTemplateId: _.get(
      state,
      "assessment.assessmentData.unitPlanTemplateId"
    ),
    currentCurriculumProgramType: getCurrentCurriculumProgramType(state),
    subjectsField,
    isUnitPlanFieldDisabled,
    isCourseBasedGrading: getIsCourseBasedGradingEnabled(),
  };
};

export default compose(
  I18nHOC({}),
  connect(mapStateToProps, mapActionCreators),
  getFlatUnitPlanFeedForAssessmentEnhancer
)(UnitSelection);
